// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// Read-only blueprint view shared by the owner's detail page and the admin view
// (requirements 4.4, 11.8, 11.15): metadata, schema, versions, diff, packages, notices.
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import Link from '@cloudscape-design/components/link';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Table from '@cloudscape-design/components/table';
import Tabs from '@cloudscape-design/components/tabs';
import { describeOptionsChange, diffSchemas, schemaToForm, type BdaSchema, type Recipient } from '@bda/shared';
import type { z } from 'zod';
import type { blueprintDetailResponse } from '@bda/shared';
import { useVersion } from '../../api/hooks';
import { formatDate } from '../../lib/format';
import { FieldsEditor } from './FieldsEditor';
import { JsonPreview } from '../../components/JsonPreview';

type Detail = z.infer<typeof blueprintDetailResponse>;
type SetMembership = NonNullable<Detail['sets']>[number];

export const USED_IN_SETS_HEADER = 'Used in blueprint sets';

/** The warning for a Set_Draft whose Members now share a Document_Type with this blueprint (blueprint-sets Req 2.7). */
export const setConflictMessage = (m: SetMembership): string =>
  m.conflict
    ? `In the set "${m.setName}", this blueprint and "${m.conflict.otherBlueprintName}" both have the document type "${m.conflict.documentType}". BDA cannot tell them apart, so runs of the draft set are rejected until one of them changes.`
    : '';

/** One warning per set with a Document_Type conflict, each with a link to the set (Req 2.7). Renders nothing without. */
export function SetConflictWarnings({ sets }: { sets: readonly SetMembership[] | undefined }) {
  const navigate = useNavigate();
  const conflicts = (sets ?? []).filter((m) => m.conflict);
  if (conflicts.length === 0) return null;
  return (
    <SpaceBetween size="xs">
      {conflicts.map((m) => (
        <Alert
          key={m.setId}
          type="warning"
          header="Document type conflict in a blueprint set"
          action={
            <Button onClick={() => navigate(`/sets/${m.setId}`)} ariaLabel={`Open set ${m.setName}`}>
              Open set
            </Button>
          }
        >
          {setConflictMessage(m)}
        </Alert>
      ))}
    </SpaceBetween>
  );
}

/** "Used in blueprint sets": the owner's sets with this blueprint as a Member and the Member's ref (Req 1.7). */
function SetMembershipTable({ sets }: { sets: readonly SetMembership[] }) {
  const navigate = useNavigate();
  return (
    <Table
      header={
        <Header variant="h2" counter={`(${sets.length})`}>
          {USED_IN_SETS_HEADER}
        </Header>
      }
      items={[...sets]}
      trackBy="setId"
      empty={<Box>This blueprint is not in any blueprint set.</Box>}
      columnDefinitions={[
        {
          id: 'name',
          header: 'Set',
          isRowHeader: true,
          cell: (m) => (
            <Link
              href={`/sets/${m.setId}`}
              onFollow={(e) => {
                e.preventDefault();
                navigate(`/sets/${m.setId}`);
              }}
            >
              {m.setName}
            </Link>
          ),
        },
        { id: 'ref', header: 'Version used', cell: (m) => (m.ref === 'DRAFT' ? 'Current draft' : `v${m.ref}`) },
        {
          id: 'conflict',
          header: 'Document type',
          cell: (m) =>
            m.conflict ? (
              <StatusIndicator type="warning">Same as {m.conflict.otherBlueprintName}</StatusIndicator>
            ) : (
              <StatusIndicator type="success">No conflict</StatusIndicator>
            ),
        },
      ]}
    />
  );
}

const recipientLabel = (r: Recipient): string => (r.type === 'USER' ? r.email : `Group: ${r.group}`);

export function SchemaView({ schema }: { schema: BdaSchema }) {
  const res = schemaToForm(schema);
  return (
    <Tabs
      tabs={[
        {
          id: 'form',
          label: 'Fields',
          content: res.form ? (
            <FieldsEditor fields={res.form.fields} errors={{}} dispatch={() => undefined} readOnly />
          ) : (
            <Box>This schema uses JSON features the form can't show. See the JSON tab.</Box>
          ),
        },
        { id: 'json', label: 'JSON', content: <JsonPreview value={schema} /> },
      ]}
    />
  );
}

function VersionPicker({
  label,
  versions,
  value,
  onChange,
}: {
  label: string;
  versions: number[];
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  const options = [
    { value: 'draft', label: 'Current draft' },
    ...versions.map((v) => ({ value: String(v), label: `Version ${v}` })),
  ];
  const selected = options.find((o) => o.value === (value === null ? 'draft' : String(value))) ?? options[0]!;
  return (
    <Select
      ariaLabel={label}
      selectedOption={selected}
      options={options}
      onChange={(e) =>
        onChange(e.detail.selectedOption.value === 'draft' ? null : Number(e.detail.selectedOption.value))
      }
    />
  );
}

export function DiffView({ detail }: { detail: Detail }) {
  const nums = detail.versions.map((v) => v.version);
  const [a, setA] = useState<number | null>(nums[1] ?? nums[0] ?? null);
  const [b, setB] = useState<number | null>(nums.length > 1 ? (nums[0] ?? null) : null);
  const va = useVersion(detail.blueprint.id, a);
  const vb = useVersion(detail.blueprint.id, b);
  const schemaA = a === null ? detail.blueprint.workingSchema : va.data?.schema;
  const schemaB = b === null ? detail.blueprint.workingSchema : vb.data?.schema;
  const rows = schemaA && schemaB ? diffSchemas(schemaA as BdaSchema, schemaB as BdaSchema) : [];
  return (
    <SpaceBetween size="m">
      <ColumnLayout columns={2}>
        <VersionPicker label="Compare from" versions={nums} value={a} onChange={setA} />
        <VersionPicker label="Compare to" versions={nums} value={b} onChange={setB} />
      </ColumnLayout>
      <Table
        variant="embedded"
        items={rows}
        empty={<Box>No differences.</Box>}
        loading={va.isLoading || vb.isLoading}
        columnDefinitions={[
          { id: 'path', header: 'Field', cell: (r) => <code>{r.path}</code> },
          {
            id: 'kind',
            header: 'Change',
            cell: (r) =>
              r.kind === 'options'
                ? `Allowed values changed. ${describeOptionsChange(r)}`
                : {
                    added: 'Added',
                    removed: 'Removed',
                    type: 'Type changed',
                    instruction: 'Instruction changed',
                    inferenceType: 'Inference changed',
                  }[r.kind],
          },
          { id: 'before', header: 'Before', cell: (r) => r.before ?? '-' },
          { id: 'after', header: 'After', cell: (r) => r.after ?? '-' },
        ]}
      />
    </SpaceBetween>
  );
}

/**
 * `showSets`: the owner's detail page with blueprint sets enabled shows "Used in blueprint sets" and Document_Type
 * conflict warnings (blueprint-sets Req 1.7, 2.7); the admin view leaves it off.
 */
export function BlueprintView({
  detail,
  actions,
  showNotices,
  showSets = false,
}: {
  detail: Detail;
  actions?: ReactNode;
  showNotices: boolean;
  showSets?: boolean;
}) {
  const navigate = useNavigate();
  const b = detail.blueprint;
  return (
    <SpaceBetween size="l">
      <Header variant="h1" description={b.description} actions={actions}>
        {b.name}
      </Header>
      {b.archived && <Alert type="info">This blueprint is archived.</Alert>}
      {showSets && <SetConflictWarnings sets={detail.sets} />}
      {showNotices &&
        detail.notices.map((n) => (
          <Alert key={`${n.at}${n.adminSub}`} type="info" header="Admin activity">
            {n.adminName}{' '}
            {n.action === 'DUPLICATED'
              ? 'copied this blueprint into their library'
              : 'created a handoff package from this blueprint'}{' '}
            on {formatDate(n.at)}.
          </Alert>
        ))}
      <Container header={<Header variant="h2">Details</Header>}>
        <KeyValuePairs
          columns={4}
          items={[
            { label: 'Document type', value: b.documentType },
            { label: 'Owner', value: b.ownerEmail },
            { label: 'Latest version', value: b.latestVersion ? `v${b.latestVersion}` : 'Not published' },
            { label: 'Runs', value: b.runCount },
            { label: 'Created', value: formatDate(b.createdAt) },
            { label: 'Last modified', value: formatDate(b.updatedAt) },
            { label: 'Copied from', value: b.copiedFrom ? b.copiedFrom.ownerEmail : '-' },
            {
              label: 'BDA draft blueprint ARN',
              value: b.bdaArn ? <code>{b.bdaArn}</code> : 'Created when the draft is next saved or run',
            },
          ]}
        />
      </Container>
      <Container header={<Header variant="h2">Current draft schema</Header>}>
        <SchemaView schema={b.workingSchema as BdaSchema} />
      </Container>
      <Table
        header={
          <Header variant="h2" counter={`(${detail.versions.length})`}>
            Published versions
          </Header>
        }
        items={detail.versions}
        empty={<Box>No published versions yet.</Box>}
        columnDefinitions={[
          { id: 'v', header: 'Version', cell: (v) => `v${v.version}`, isRowHeader: true },
          { id: 'at', header: 'Published', cell: (v) => formatDate(v.publishedAt) },
          { id: 'by', header: 'By', cell: (v) => v.publishedBy },
          { id: 'note', header: 'Change note', cell: (v) => v.changeNote || '-' },
        ]}
      />
      <Container header={<Header variant="h2">Compare versions</Header>}>
        <DiffView detail={detail} />
      </Container>
      {showSets && <SetMembershipTable sets={detail.sets ?? []} />}
      <Table
        header={
          <Header variant="h2" counter={`(${detail.packages.length})`}>
            Handoff packages
          </Header>
        }
        items={detail.packages}
        empty={<Box>No handoff packages yet.</Box>}
        columnDefinitions={[
          {
            id: 'title',
            header: 'Title',
            isRowHeader: true,
            cell: (p) => (
              <Link
                href={`/packages/${p.id}`}
                onFollow={(e) => {
                  e.preventDefault();
                  navigate(`/packages/${p.id}`);
                }}
              >
                {p.title}
              </Link>
            ),
          },
          { id: 'v', header: 'Version', cell: (p) => `v${p.version}` },
          { id: 'author', header: 'Author', cell: (p) => p.authorEmail },
          {
            id: 'to',
            header: 'Shared with',
            cell: (p) => (p.recipients.length ? p.recipients.map(recipientLabel).join(', ') : 'Not shared'),
          },
          { id: 'at', header: 'Created', cell: (p) => formatDate(p.createdAt) },
        ]}
      />
    </SpaceBetween>
  );
}
